'use client';

import {
  Box,
  DialogContent,
  DialogTitle,
  Drawer,
  Link,
  Loading,
  ModalClose,
  Stack,
  Typography,
  useBreakpoints,
} from '@castlery/fortress';
import { AssemblyFile, ProductProperty, selectVariantIds } from '@castlery/modules-product-domain';
import { getAssemblerInstructionCommand } from '@castlery/modules-product-services';
import { FortressVideo } from '@castlery/shared-components';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { useCallback, useState } from 'react';
import { useAsync } from 'react-use';
import { StandardInfo } from '@castlery/fortress/Icons';
import { EVENT_PDP_DETAILS } from '@castlery/modules-tracking-services';

interface ProductAIPropertyProps {
  property: ProductProperty;
}

export const ProductAIProperty = (props: ProductAIPropertyProps) => {
  const { property } = props;
  const { mobile } = useBreakpoints();
  const dispatch = useAppDispatch();
  const currentVariantIds = useAppSelector(selectVariantIds);
  const [open, setOpen] = useState(false);
  const aiData = useAsync(async () => {
    const res = await dispatch(getAssemblerInstructionCommand()).unwrap();
    return res;
  }, [currentVariantIds, dispatch]);

  const handleTrackPDPDetails = useCallback(async () => {
    await dispatch(
      EVENT_PDP_DETAILS({
        action: 'product_property',
        label: `${property.presentation} - ${property.value}`,
      })
    );
  }, [dispatch, property]);

  const handleOpenDrawer = useCallback(() => {
    setOpen(true);
    handleTrackPDPDetails();
  }, []);

  return (
    <Box>
      {aiData.loading ? (
        <Loading theme="dark" />
      ) : (
        <>
          <Typography level="body2">
            {property.value}
            {(!!aiData.value?.aiDocs?.length || !!aiData.value?.aiVideos?.length) && (
              <StandardInfo
                role="button"
                onClick={handleOpenDrawer}
                sx={{
                  verticalAlign: 'top',
                  display: 'inline-block',
                  ml: 2,
                  cursor: 'pointer',
                  width: '20px',
                  height: '20px',
                }}
              />
            )}
          </Typography>
        </>
      )}
      <ProductAIDrawer open={open} onClose={() => setOpen(false)} aiData={aiData?.value} />
    </Box>
  );
};

interface ProductAIDrawerProps {
  open: boolean;
  onClose: () => void;
  aiData?: {
    aiVideos: AssemblyFile[];
    aiDocs: AssemblyFile[];
  };
}

const ProductAIDrawer = (props: ProductAIDrawerProps) => {
  const { open, onClose, aiData } = props;
  const { desktop } = useBreakpoints();
  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor={desktop ? 'right' : 'bottom'}
      size="md"
      sx={{
        ...(!desktop && {
          '--Drawer-verticalSize': '80vh',
        }),
      }}
    >
      <DialogTitle component={Box}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {!((aiData?.aiDocs?.length || 0) >= 1 && (aiData?.aiVideos?.length || 0) >= 1) ? (
            <Typography level="h3">Assembly Instructions</Typography>
          ) : null}
          <ModalClose />
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 6 }}>
        <Stack>
          {aiData?.aiVideos?.length ? (
            <>
              <Typography level="h4">Assembly Videos</Typography>
              <Stack>
                {aiData?.aiVideos?.map((item) => (
                  <Box key={item.id}>
                    <FortressVideo
                      id={item.id.toString()}
                      key={item.id}
                      containerConfig={{
                        aspectRatio: 1,
                        objectFit: 'fill',
                      }}
                      autoPlay={false}
                      src={item?.file_link}
                    />
                  </Box>
                ))}
              </Stack>
            </>
          ) : null}
          {aiData?.aiDocs?.length ? (
            <>
              {/* <Typography level="h4">Assembly Instructions</Typography> */}
              <Stack>
                {aiData?.aiDocs?.map((item) => (
                  <Stack key={item.id} direction="row" alignItems="center">
                    <Typography level="body2">{`View `}</Typography>
                    <Link
                      variant="primary"
                      level="body2"
                      href={item?.file_link}
                      target="_blank"
                      sx={{
                        mx: 1,
                      }}
                    >
                      {item?.display_filename}
                    </Link>
                    <Typography level="body2">(PDF)</Typography>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : null}
        </Stack>
      </DialogContent>
    </Drawer>
  );
};
